import React, { useState, useEffect } from 'react';
import { WhatsAppNotificationConfig } from '../types/whatsapp';
import whatsappService from '../services/whatsappService';
import ConfigurationForm from '../components/WhatsApp/ConfigurationForm';
import ConfigurationList from '../components/WhatsApp/ConfigurationList';
import NotificationStatus from '../components/WhatsApp/NotificationStatus';
import LogsPanel from '../components/WhatsApp/LogsPanel';

const WhatsAppConfig: React.FC = () => {
  const [configurations, setConfigurations] = useState<WhatsAppNotificationConfig[]>([]);
  const [selectedConfig, setSelectedConfig] = useState<WhatsAppNotificationConfig | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadConfigurations();
  }, []);

  const loadConfigurations = async () => {
    try {
      setLoading(true);
      const configs = await whatsappService.getConfigurations();
      setConfigurations(configs);
    } catch (err) {
      setError('Error cargando configuraciones');
      console.error('Error loading configurations:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = () => {
    setSelectedConfig(null);
    setShowForm(true);
  };

  const handleEdit = (config: WhatsAppNotificationConfig) => {
    setSelectedConfig(config);
    setShowForm(true);
  };

  const handleDelete = async (configId: number) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar esta configuración?')) {
      try {
        await whatsappService.deleteConfiguration(configId);
        await loadConfigurations();
      } catch (err) {
        setError('Error eliminando configuración');
        console.error('Error deleting configuration:', err);
      }
    }
  };

  const handleActivate = async (configId: number) => {
    try {
      await whatsappService.activateConfiguration(configId);
      await loadConfigurations();
    } catch (err) {
      setError('Error activando configuración');
      console.error('Error activating configuration:', err);
    }
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setSelectedConfig(null);
    loadConfigurations();
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setSelectedConfig(null);
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto py-6 px-2 sm:px-6 lg:px-8">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-6 px-2 sm:px-6 lg:px-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Configuración de Notificaciones WhatsApp</h1>
        <p className="mt-2 text-gray-600">
          Gestiona las configuraciones de notificaciones automáticas por WhatsApp
        </p>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <svg className="flex-shrink-0 h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
            <div className="ml-auto pl-3">
              <button
                onClick={() => setError(null)}
                className="inline-flex text-red-400 hover:text-red-600"
              >
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Estado de notificaciones */}
      <div className="mb-8">
        <NotificationStatus />
      </div>

      {/* Historial de mensajes */}
      <div className="mb-8">
        <LogsPanel />
      </div>

      {showForm ? (
        <ConfigurationForm
          config={selectedConfig}
          onSuccess={handleFormSuccess}
          onCancel={handleFormCancel}
        />
      ) : (
        <div>
          <div className="mb-6">
            <button
              onClick={handleCreateNew}
              className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Nueva Configuración
            </button>
          </div>

          <ConfigurationList
            configurations={configurations}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onActivate={handleActivate}
          />
        </div>
      )}
    </div>
  );
};

export default WhatsAppConfig;
